import { useEffect, useState } from "react";

import { Tooltip } from "antd";

import { RVButton, RVColumn, RVIcon, RVIconSizeOptions, RVLearnMoreLink, RVRow } from "@regal-voice/ui";
import { getSseConnectionSnapshot } from "Services/server-sent-events";

import type { SseEchoDiagnostic, SseEchoStatus } from "./useSseEchoDiagnostic";

import styles from "./SseConnectionDiagnostic.module.scss";

export const SSE_HARDWARE_NETWORK_REQUIREMENTS_URL =
    "https://support.regal.ai/hc/en-us/articles/6744950315035-Hardware-and-network-requirements";

const HELP_LINK_LABEL = "Hardware and network requirements";

const READY_STATE_LABELS: Record<number, string> = {
    0: "Connecting",
    1: "Open",
    2: "Closed",
};

const STATUS_LABELS: Record<SseEchoStatus, string> = {
    idle: "Idle",
    checking: "Checking…",
    healthy: "Connected",
    broken: "No response",
};

function formatAbsolute(ts?: number): string {
    if (!ts) {
        return "—";
    }
    return new Date(ts).toLocaleTimeString();
}

function formatRelative(ts: number | undefined, now: number): string {
    if (!ts) {
        return "";
    }
    const seconds = Math.max(0, Math.round((now - ts) / 1000));
    if (seconds < 1) {
        return " (just now)";
    }
    return ` (${seconds}s ago)`;
}

type DiagnosticPanelProps = {
    active: boolean;
    diagnostic: SseEchoDiagnostic;
};

export function DiagnosticPanel({ active, diagnostic }: DiagnosticPanelProps): JSX.Element {
    const { status, lastSuccessAt, lastRoundTripMs, runNow } = diagnostic;
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!active) {
            return;
        }
        setNow(Date.now());
        const tickId = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(tickId);
    }, [active]);

    const snapshot = getSseConnectionSnapshot();
    const readyStateLabel =
        snapshot.readyState === undefined ? "Unknown" : (READY_STATE_LABELS[snapshot.readyState] ?? "Unknown");

    const dotClass =
        status === "healthy" ? styles.dotHealthy : status === "broken" ? styles.dotBroken : styles.dotNeutral;

    return (
        <RVColumn gap={2} className={styles.panel} data-testid="sse-diagnostic-panel">
            <RVRow gap={2} alignItems="center">
                <span className={`${styles.dot} ${dotClass}`} data-testid="sse-diagnostic-dot" />
                <span className={styles.title}>SSE Connection</span>
                <Tooltip placement="right" title={HELP_LINK_LABEL} trigger={["hover", "focus"]}>
                    <RVLearnMoreLink
                        aria-label={HELP_LINK_LABEL}
                        className={styles.helpLink}
                        data-testid="sse-diagnostic-help"
                        href={SSE_HARDWARE_NETWORK_REQUIREMENTS_URL}
                    >
                        <RVIcon icon="info-circle" size={RVIconSizeOptions.SMALL} />
                    </RVLearnMoreLink>
                </Tooltip>
                <span className={styles.statusLabel}>{STATUS_LABELS[status]}</span>
            </RVRow>

            <RVColumn gap={1} className={styles.details}>
                <RVRow gap={2} justifyContent="space-between">
                    <span className={styles.label}>Last heartbeat ping</span>
                    <span className={styles.value} data-testid="sse-diagnostic-last-ping">
                        {formatAbsolute(snapshot.lastPingAt)}
                        {formatRelative(snapshot.lastPingAt, now)}
                    </span>
                </RVRow>

                <RVRow gap={2} justifyContent="space-between">
                    <span className={styles.label}>Last successful test</span>
                    <span className={styles.value} data-testid="sse-diagnostic-last-success">
                        {formatAbsolute(lastSuccessAt)}
                        {formatRelative(lastSuccessAt, now)}
                    </span>
                </RVRow>

                <RVRow gap={2} justifyContent="space-between">
                    <span className={styles.label}>Round trip</span>
                    <span className={styles.value}>{lastRoundTripMs === undefined ? "—" : `${lastRoundTripMs}ms`}</span>
                </RVRow>

                <RVRow gap={2} justifyContent="space-between">
                    <span className={styles.label}>Connection state</span>
                    <span className={styles.value}>{readyStateLabel}</span>
                </RVRow>
            </RVColumn>

            <RVButton type="default" size="small" onClick={runNow} data-testid="sse-diagnostic-run-now">
                Run test now
            </RVButton>
        </RVColumn>
    );
}
